import Dexie, { IndexableType } from 'dexie'
import { DatabaseUtilityService, IndexableTypeArray, IndexableTypePart } from './databaseUtility.service'
import { applyEncryptionMiddleware, cryptoOptions } from 'dexie-encrypted'
import Logger from './logger.service'

export const DB_NAME_PREFIX = 'zoll_now_'
export const DB_NAME_GLOBAL = 'zoll_global'
export const DB_NAME_SETTINGS = 'zoll_settings'
export const DB_NAME_SESSION_PERSISTENCE = 'zoll_session_persistance'

export interface IOrderBy {
  orderBy: string
  asc: boolean
}

export class IndexDbService {
  public db: Dexie

  constructor(database: string) {
    this.db = new Dexie(database)

    if (database.includes(DB_NAME_PREFIX)) {
      const encryptionKey = DatabaseUtilityService.getEncryptionKeyForServiceDB(database)

      applyEncryptionMiddleware(
        this.db,
        encryptionKey,
        {
          pcrDocs: {
            type: cryptoOptions.ENCRYPT_LIST,
            fields: ['data'],
          },
        } as any,
        this.encryptionKeyHasChanged
      )
    }

    if (database === DB_NAME_SETTINGS) {
      const encryptionKey = DatabaseUtilityService.getEncryptionKeyForSettingsDB(database)

      applyEncryptionMiddleware(
        this.db,
        encryptionKey,
        {
          deviceInfo: {
            type: cryptoOptions.ENCRYPT_LIST,
            fields: ['salt', 'uuid'],
          },
        } as any,
        this.encryptionKeyHasChanged
      )
    }

    if (database === DB_NAME_SESSION_PERSISTENCE) {
      const encryptionKey = DatabaseUtilityService.getEncryptionKeyForSettingsDB(database)

      applyEncryptionMiddleware(
        this.db,
        encryptionKey,
        {
          persistedMetadata: {
            type: cryptoOptions.ENCRYPT_LIST,
            fields: ['metadata'],
          },
        } as any,
        this.encryptionKeyHasChanged
      )
    }
  }

  async encryptionKeyHasChanged(db: Dexie): Promise<void[]> {
    // For now, just log this event rather than calling the built-in functions: clearAllTables or clearEncryptedTables
    // In the future, if we ever need to change the encryption key, we can add code here to migrate the records from the old key to the new key.
    Logger.warn('Encryption key has changed!!!')
    Logger.warn(`Key change for DB: ${db?.name}`)

    return []
  }

  async getAll<T>(table: string): Promise<Array<T>> {
    return this.db.table(table).toArray()
  }

  async get<T>(key: string | number, table: string): Promise<T> {
    return this.db.table(table).get(key)
  }

  async getWhereAttributeEquals<T>(
    attribute: string,
    value: IndexableTypePart | IndexableTypeArray,
    table: string,
    orderBy?: IOrderBy
  ): Promise<T[]> {
    const results = this.db.table(table).where(attribute).equals(value)

    if (orderBy) {
      if (orderBy.asc) {
        return results.sortBy(orderBy.orderBy)
      }

      return (await results.sortBy(orderBy.orderBy)).reverse()
    }

    return results.toArray()
  }

  async getWhereAttributeStartsWith<T>(
    attribute: string,
    value: string,
    table: string,
    orderBy?: IOrderBy
  ): Promise<T[]> {
    const results = this.db.table(table).where(attribute).startsWith(value)

    if (orderBy) {
      if (orderBy.asc) {
        return results.sortBy(orderBy.orderBy)
      }

      return (await results.sortBy(orderBy.orderBy)).reverse()
    }

    return results.toArray()
  }

  async getPrimaryKeysByIndex(index: string, value: any, table: string): Promise<IndexableType[]> {
    return this.db.table(table).where(index).equals(value).primaryKeys()
  }

  async save<T>(document: T, table: string): Promise<IndexableType> {
    return this.db.table(table).put(document)
  }

  async add<T>(document: T, table: string) {
    return this.db.table(table).add(document)
  }

  async addAll<T>(document: T[], table: string) {
    return this.db.table(table).bulkAdd(document)
  }

  async updateAll<T>(document: T[], table: string) {
    return this.db.table(table).bulkPut(document)
  }

  async deleteAll(keys: IndexableType[], table: string) {
    return this.db.table(table).bulkDelete(keys)
  }

  async clearTable(table: string) {
    return this.db.table(table).clear()
  }

  async addWithKey<T>(document: T, table: string, key: string) {
    return this.db.table(table).add(document, key)
  }

  async update(key: string, document: any, table: string) {
    return this.db.table(table).update(key, document)
  }

  async createOrUpdate(key: string, document: any, table: string) {
    return this.db.table(table).put(document, key)
  }

  async remove(key: string | number, table: string): Promise<void> {
    return this.db.table(table).delete(key)
  }

  async getCount(table: string): Promise<number> {
    return this.db.table(table).count()
  }

  async isEmpty(table: string): Promise<boolean> {
    const count = await this.getCount(table)
    return count === 0
  }
}
